// src/components/ObjectDetailsTab.js
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Typography,
  Checkbox,
  Select,
  Input,
  Button,
  Affix,
  Avatar,
  Spin,
  message
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';

const { Title } = Typography;
const { Option } = Select;

const ObjectDetailsTab = ({ record }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [objectData, setObjectData] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [dataset, setDataset] = useState([]);
  const [isEditable, setIsEditable] = useState(false);
  const [notificationToFields, setNotificationToFields] = useState(null);

  const fieldSecurity = useSelector((state) => state.data.items[0]);

  // Fetch object details
  useEffect(() => {
    const fetchObjectDetails = async () => {
      setLoading(true);
      try {
        const apiService = new ApiService(
          `${BASE_URL}/mt_objects/${record._id}`,
          { 'Content-Type': 'application/json' },
          'GET'
        );
        const response = await apiService.makeCall(navigate);
        if (response) {
          const { search_layout_fields, recordCount, _id, is_system, key, ...rest } = response;
          setObjectData(rest);
          setInitialValues(rest);
          form.setFieldsValue(rest);
          buildDataset(rest);
        }
      } catch (error) {
        message.error('Failed to fetch object details');
      } finally {
        setLoading(false);
      }
    };

    if (record?._id) {
      fetchObjectDetails();
    }
  }, [record]);

useEffect(() => {
  const fetchFields = async () => {
    try {
      const apiService = new ApiService(
        `${BASE_URL}/mt_fields/object/${record.name}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiService.makeCall();
      // ✅ Filter only email or lookup(User) fields
      const fields=
        response.filter(
          (field) =>
            field.type === 'email' ||
            (field.type === 'lookup' && field.parent_object_name === 'User')
        );

        setNotificationToFields(fields);
      
    } catch (error) {
    }
  };

  if (record?._id) {
    fetchFields();
  }
}, [record?._id]);


  const buildDataset = (values) => {
    setDataset([
      { label: 'Label', value: values?.label || '', fieldName: 'label' },
      { label: 'Name', value: values?.name || '', fieldName: 'name' },
      { label: 'Plural Label', value: values?.pluralLabel || '', fieldName: 'pluralLabel' },
      { label: 'Object Description', value: values?.description || '', fieldName: 'description' },
      { label: 'Record Creation View', value: values?.record_creation_view || '', fieldName: 'record_creation_view', isSelect: true },
      { label: 'Default Access', value: values?.default_access || 'private', fieldName: 'default_access', isSelect: true },
      { label: 'Allow Role Hierarchy', value: values?.allow_role_hierarchy, isCheckbox: true, fieldName: 'allow_role_hierarchy' },
      { label: 'Allow Record Level Sharing', value: values?.allow_record_level_sharing, isCheckbox: true, fieldName: 'allow_record_level_sharing' },
      { label: 'Allow Search', value: values?.allow_search, isCheckbox: true, fieldName: 'allow_search' },
      { label: 'Track Activities', value: values?.track_activities, isCheckbox: true, fieldName: 'track_activities' },
      { label: 'Track Field History', value: values?.track_field_history, isCheckbox: true, fieldName: 'track_field_history' },
      { label: 'Allow File Upload', value: values?.allow_files, isCheckbox: true, fieldName: 'allow_files' },
      { label: 'Allow Approvals', value: values?.allow_approvals, isCheckbox: true, fieldName: 'allow_approvals' },
      { label: 'Allow Comments', value: values?.allow_comments, isCheckbox: true, fieldName: 'allow_comments' },
      { label: 'Notification to', value: values?.notification_to, isSelect: true, fieldName: 'notification_to' },

    ]);
  };

  const handleEditClick = () => {
    setIsEditable(true);
    form.setFieldsValue(objectData);
  };

  const handleCancelClick = () => {
    form.setFieldsValue(initialValues);
    buildDataset(initialValues);
    setIsEditable(false);
  };

  const handleValuesChange = (changedValues, allValues) => {
    form.setFieldsValue(allValues);
    const [fieldName, value] = Object.entries(changedValues)[0];
    setDataset((prev) =>
      prev.map((field) =>
        field.fieldName === fieldName ? { ...field, value } : field
      )
    );
  };

  const handleSubmit = async () => {
    form.submit();
  };

  const handleFinish = async (values) => {
    setLoading(true);

    const formData = {};
    for (const key in values) {
      if (values[key] !== initialValues[key]) {
        formData[key] = values[key];
      }
    }

    if (Object.keys(formData).length === 0) {
      message.warning('No changes detected to save.');
      setLoading(false);
      return;
    }

    try {
      const apiService = new ApiService(
        `${BASE_URL}/mt_objects/${record._id}`,
        { 'Content-Type': 'application/json' },
        'PUT',
        { mt_object: formData }
      );
      await apiService.makeCall(navigate);
      message.success('Object updated successfully');
      setIsEditable(false);
      setInitialValues(values);
      buildDataset(values);
    } catch (error) {
      message.error('Failed to save object');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading} tip="Loading...">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
      >
        <Title level={4} >Details</Title>
        <Row gutter={12}>
          {dataset.map((field, index) => (
            <Col xs={24} sm={12} key={index}>
              <Form.Item
                label={<span style={{ fontSize: '12px' }}>{field?.label}</span>}
                name={field.fieldName}
                valuePropName={field.isCheckbox ? 'checked' : 'value'}
              >
                <div
                  className="editable-field-container"

                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: !isEditable ? '1px solid #ddd' : 'none',
                    minHeight: 25
                  }}
                >
                  {field.isCheckbox ? (
                    isEditable ? (
                      <Checkbox checked={field.value} />
                    ) : (
                      <Checkbox checked={field.value} disabled />
                    )
                  ) : field.isSelect ? (
                    isEditable ? (
                      <Select
                        value={field.value}
                        onChange={(value) => {
                          form.setFieldsValue({ [field.fieldName]: value });
                          handleValuesChange(
                            { [field.fieldName]: value },
                            form.getFieldsValue()
                          );
                        }}
                        style={{ minWidth: 90 }}
                      >
                        {field.fieldName === 'default_access' ? (
                          <>
                            <Option value="private">Private</Option>
                            <Option value="public_read">Public Read</Option>
                            <Option value="public_read_write">Public Read Write</Option>
                          </>
                        ) : field.fieldName === 'notification_to' ? (
                          notificationToFields?.map((nf) => (
                            <Option key={nf.name} value={nf.name}>
                              {nf.label}
                            </Option>
                          ))
                        ) : (
                          <>
                            <Option value="Modal">Modal</Option>
                            <Option value="Drawer">Drawer</Option>
                          </>
                        )}

                      </Select>
                    ) : (
                      <span>{field.value}</span>
                    )
                  ) : isEditable && field.fieldName !== 'name' ? (
                    <Input value={field.value} />
                  ) : (
                    <span>{field.value}</span>
                  )}
                  {!isEditable &&
                    fieldSecurity?.system_settings?.objects?.edit && (
                      <Avatar
                        icon={<EditOutlined />}
                        className="edit-icon"
                        size={20}
                        style={{ cursor: 'pointer', backgroundColor: 'transparent', color: 'black' }}
                        onClick={handleEditClick}
                      />
                    )}
                </div>
              </Form.Item>
            </Col>
          ))}
        </Row>

        {isEditable && (
          <Affix offsetBottom={0}>
            <div
              style={{
                width: '79.9%',
                padding: '10px 0',
                background: 'rgba(240, 242, 245, 0.9)',
                position: 'fixed',
                bottom: '0px',
              }}
            >
              <Row justify="center">
                <Button onClick={handleCancelClick} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button type="primary" onClick={handleSubmit}>
                  Save
                </Button>
              </Row>
            </div>
          </Affix>
        )}
      </Form>
    </Spin>
  );
};

export default ObjectDetailsTab;
